import { useContext, useMemo } from 'react'
import styled from 'styled-components'
import WatchLaterRoundedIcon from '@mui/icons-material/WatchLaterRounded'
import KanbanContext from '../../KanbanContext'
import { StatusKanbanSearchCondition } from '../../hooks/useSearchCondition'
import { dateTermVoService } from '../../../../../domain/value-object/DateTermVO'
import { intl } from '../../../../../i18n'
import { colorPalette } from '../../../../style/colorPalette'

type Props = {
  searchCondition: StatusKanbanSearchCondition
}

const SearchConditionLabels = ({ searchCondition }: Props) => {
  const context = useContext(KanbanContext)

  const teamName = useMemo(() => {
    if (!searchCondition.teamUuid) return undefined
    const team = context.teams.find(t => t.value === searchCondition.teamUuid)
    return team?.name
  }, [searchCondition.teamUuid, context.teams])

  const sprintName = useMemo(() => {
    if (!searchCondition.sprintUuid) return undefined
    const sprint = context.sprints.find(
      s => s.value === searchCondition.sprintUuid
    )
    return sprint?.name
  }, [searchCondition.sprintUuid, context.sprints])

  const scheduledEndDateString = useMemo(() => {
    if (!searchCondition.scheduledEndDate) return undefined
    const { from, to } = searchCondition.scheduledEndDate
    if (!from && !to) return undefined
    const formatted = dateTermVoService.format(
      searchCondition.scheduledEndDate,
      'YYYY/MM/DD'
    )
    return `${formatted.from || ''}~${formatted.to || ''}`
  }, [searchCondition.scheduledEndDate])

  return (
    <SearchConditionLabelsRoot>
      {teamName && (
        <SearchConditionLabel>
          <StyledWatchIcon />
          <LabelText>
            {intl.formatMessage({ id: 'wbsItemSearchFilterPanel.team' })}:{' '}
            {teamName}
          </LabelText>
        </SearchConditionLabel>
      )}
      {sprintName && (
        <SearchConditionLabel>
          <StyledWatchIcon />
          <LabelText>
            {intl.formatMessage({ id: 'wbsItemSearchFilterPanel.sprint' })}:{' '}
            {sprintName}
          </LabelText>
        </SearchConditionLabel>
      )}
      {scheduledEndDateString && (
        <SearchConditionLabel>
          <StyledWatchIcon />
          <LabelText>
            {intl.formatMessage({
              id: 'wbsItemSearchFilterPanel.scheduledEndDate',
            })}
            : {scheduledEndDateString}
          </LabelText>
        </SearchConditionLabel>
      )}
    </SearchConditionLabelsRoot>
  )
}

export default SearchConditionLabels

const SearchConditionLabelsRoot = styled('div')({
  display: 'flex',
  borderRadius: '4px',
  padding: '8px 10px',
  backgroundColor: colorPalette.monotone[1],
  flexDirection: 'row',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: '8px',
  minHeight: '36px',
})

const SearchConditionLabel = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  padding: '2px 8px',
  borderRadius: '15px',
  border: `1px dashed ${colorPalette.monotone[4]}`,
  backgroundColor: colorPalette.monotone[1],
})

const StyledWatchIcon = styled(WatchLaterRoundedIcon)({
  fontSize: '14px',
  color: colorPalette.monotone[4],
})

const LabelText = styled('span')({
  fontSize: '12px',
  color: colorPalette.monotone[10],
})
